import React, { useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import dayjs from "dayjs";
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { createStaticRanges, DateRangePicker } from "react-date-range";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const getPresetBounds = () => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  return {
    todayStart,
    todayEnd,
    yesterdayStart: startOfDay(addDays(now, -1)),
    yesterdayEnd: endOfDay(addDays(now, -1)),
    thisWeekStart: startOfWeek(now),
    lastWeekStart: startOfWeek(addDays(now, -7)),
    lastWeekEnd: endOfWeek(addDays(now, -7)),
    thisMonthStart: startOfMonth(now),
    lastMonthStart: startOfMonth(addMonths(now, -1)),
    lastMonthEnd: endOfMonth(addMonths(now, -1)),
    last7Start: startOfDay(addDays(now, -6)),
    last30Start: startOfDay(addDays(now, -29)),
  };
};

const DATE_RANGE_STATIC_RANGES = createStaticRanges([
  {
    label: "Today",
    range: () => {
      const { todayStart, todayEnd } = getPresetBounds();
      return { startDate: todayStart, endDate: todayEnd };
    },
  },
  {
    label: "Yesterday",
    range: () => {
      const { yesterdayStart, yesterdayEnd } = getPresetBounds();
      return { startDate: yesterdayStart, endDate: yesterdayEnd };
    },
  },
  {
    label: "This Week",
    range: () => {
      const { thisWeekStart, todayEnd } = getPresetBounds();
      return { startDate: thisWeekStart, endDate: todayEnd };
    },
  },
  {
    label: "Last Week",
    range: () => {
      const { lastWeekStart, lastWeekEnd } = getPresetBounds();
      return { startDate: lastWeekStart, endDate: lastWeekEnd };
    },
  },
  {
    label: "Last 7 Days",
    range: () => {
      const { last7Start, todayEnd } = getPresetBounds();
      return { startDate: last7Start, endDate: todayEnd };
    },
  },
  {
    label: "Last 30 Days",
    range: () => {
      const { last30Start, todayEnd } = getPresetBounds();
      return { startDate: last30Start, endDate: todayEnd };
    },
  },
  {
    label: "This Month",
    range: () => {
      const { thisMonthStart, todayEnd } = getPresetBounds();
      return { startDate: thisMonthStart, endDate: todayEnd };
    },
  },
  {
    label: "Last Month",
    range: () => {
      const { lastMonthStart, lastMonthEnd } = getPresetBounds();
      return { startDate: lastMonthStart, endDate: lastMonthEnd };
    },
  },
]);

export const formatRangeLabel = (start, end) => {
  if (!start || !end) {
    return "Select date range";
  }
  return `${dayjs(start).format("DD/MM/YYYY")} – ${dayjs(end).format("DD/MM/YYYY")}`;
};

export const inclusiveDayCount = (startDate, endDate) => {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    return null;
  }

  const days = dayjs(endDate)
    .startOf("day")
    .diff(dayjs(startDate).startOf("day"), "day");
  if (days < 0) {
    return null;
  }

  return days + 1;
};

const clampRangeToMaxDays = (startDate, endDate, maxDays) => {
  const start = dayjs(startDate).startOf("day");
  let end = dayjs(endDate).startOf("day");
  const today = dayjs().startOf("day");

  if (end.isAfter(today)) {
    end = today;
  }
  if (end.isBefore(start)) {
    end = start;
  }

  if (end.diff(start, "day") > maxDays - 1) {
    end = start.add(maxDays - 1, "day");
    if (end.isAfter(today)) {
      end = today;
    }
  }

  return { start, end };
};

const TimePeriodField = ({
  startDate,
  endDate,
  onChange,
  maxDays,
  label = "Time Period",
  required = false,
  error = false,
  helperText,
  sx,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const dayCount = inclusiveDayCount(startDate, endDate);
  const rangeInvalid = dayCount === null || dayCount > maxDays;

  const dateRangeSelection = useMemo(
    () => [
      {
        startDate: startDate ? dayjs(startDate).toDate() : new Date(),
        endDate: endDate ? dayjs(endDate).toDate() : new Date(),
        key: "selection",
      },
    ],
    [startDate, endDate],
  );

  const handleDateRangeChange = (item) => {
    const selection = item?.selection;
    if (!selection?.startDate || !selection?.endDate) {
      return;
    }

    const { start, end } = clampRangeToMaxDays(
      selection.startDate,
      selection.endDate,
      maxDays,
    );
    onChange(start, end);
  };

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={label}
        required={required}
        value={formatRangeLabel(startDate, endDate)}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        error={Boolean(error) || rangeInvalid}
        helperText={helperText}
        slotProps={{
          htmlInput: { readOnly: true },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <CalendarMonthOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          cursor: "pointer",
          "& .MuiInputBase-input": { cursor: "pointer" },
          ...sx,
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <DateRangePicker
          ranges={dateRangeSelection}
          onChange={handleDateRangeChange}
          moveRangeOnFirstSelection={false}
          months={1}
          direction="horizontal"
          maxDate={new Date()}
          dateDisplayFormat="dd/MM/yyyy"
          rangeColors={[theme.palette.primary.main]}
          staticRanges={DATE_RANGE_STATIC_RANGES}
          inputRanges={[]}
        />
      </Popover>
      <Typography
        variant="caption"
        color={rangeInvalid ? "error" : "text.secondary"}
        sx={{ mt: 0.5, display: "block" }}
      >
        {dayCount !== null
          ? `${dayCount} days selected (max ${maxDays})`
          : `Select a time period (max ${maxDays} days)`}
      </Typography>
    </Box>
  );
};

export default TimePeriodField;
